import type { Content } from "@/app/queries";
import { plural } from "@/lib/plural";
import { CRITERION_KINDS, DashedButton, IconButton, INPUT, type Patch } from "./shared";
import { BankImport } from "./BankImport";

type Criterion = Content["gate"]["criteria"][number];

/** Вкладка «гейт»: критерии с видом и порогом, банк вопросов. */
export function GateTab({ content, patch, opens }: { content: Content; patch: (p: Patch) => void; opens: string }) {
  const { criteria, bank } = content.gate;
  const setCriterion = (i: number, fn: (c: Criterion) => Criterion) => patch((c) => ({ ...c, gate: { ...c.gate, criteria: c.gate.criteria.map((x, j) => (j === i ? fn(x) : x)) } }));
  return (
    <section className="overflow-hidden rounded-md border border-bd2 bg-card">
      <div className="flex items-center gap-2.5 border-b border-bd bg-bg2 px-4 py-3">
        <span className="font-mono text-[11px] uppercase tracking-[.06em] text-mut">гейт «Готов к переходу»</span>
        <span className="flex-1" />
        <span className="font-mono text-[11px] text-mut">{opens}</span>
      </div>
      <div className="flex flex-col gap-2.5 px-4 py-3.5">
        {criteria.map((cr, i) => (
          <div key={cr.id || `c-${i}`} className="grid grid-cols-[22px_minmax(0,1fr)_168px_26px] items-center gap-[11px]" data-testid="criterion">
            <span className="cursor-grab font-mono text-[12px] text-mut" aria-hidden>
              ⠿
            </span>
            <input aria-label={`критерий ${i + 1}`} value={cr.text} onChange={(e) => setCriterion(i, (c) => ({ ...c, text: e.target.value }))} className={`${INPUT} w-full font-sans text-[13px] text-ink2`} />
            <span className="flex items-center gap-1.5">
              <select
                aria-label={`вид критерия ${i + 1}`}
                value={cr.kind}
                onChange={(e) => setCriterion(i, (c) => ({ ...c, kind: e.target.value as Criterion["kind"], threshold: e.target.value === "practice_share" ? (c.threshold ?? 0.8) : undefined }))}
                className={`${INPUT} min-w-0 flex-1 py-[7px] font-mono text-[11px]`}
              >
                {CRITERION_KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
              {cr.kind === "practice_share" && (
                <span className="flex items-center gap-1 font-mono text-[11px] text-mut">
                  <input
                    aria-label={`порог критерия ${i + 1}, %`}
                    type="number"
                    min={0}
                    max={100}
                    value={Math.round((cr.threshold ?? 0.8) * 100)}
                    onChange={(e) => setCriterion(i, (c) => ({ ...c, threshold: Math.min(100, Math.max(0, Number(e.target.value) || 0)) / 100 }))}
                    className={`${INPUT} w-[52px] py-[7px] text-right font-mono text-[11px]`}
                  />
                  %
                </span>
              )}
            </span>
            <IconButton onClick={() => patch((c) => ({ ...c, gate: { ...c.gate, criteria: c.gate.criteria.filter((_, j) => j !== i) } }))} aria-label={`удалить критерий ${i + 1}`}>
              ×
            </IconButton>
          </div>
        ))}
        {criteria.length === 0 && <div className="text-[12.5px] text-ink2">Критериев нет — гейт нельзя сдать, пока их меньше трёх.</div>}
        <DashedButton className="w-fit text-[11.5px]" onClick={() => patch((c) => ({ ...c, gate: { ...c.gate, criteria: [...c.gate.criteria, { id: "", text: "новый критерий", kind: "manual" }] } }))}>
          + критерий
        </DashedButton>
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 border-t border-bd px-4 py-3.5">
        <div>
          <div className="mb-1.5 font-mono text-[11px] text-mut">банк вопросов</div>
          <div className="flex flex-wrap items-center gap-2 text-[13px] leading-[1.5] text-ink2">
            <span>Реальные вопросы с собеседований:</span>
            <span>«</span>
            <input aria-label="название банка вопросов" value={bank.name} onChange={(e) => patch((c) => ({ ...c, gate: { ...c.gate, bank: { ...c.gate.bank, name: e.target.value } } }))} className={`${INPUT} w-[140px] py-1 font-sans text-[13px]`} />
            <span>» ·</span>
            <input
              aria-label="число вопросов в банке"
              type="number"
              min={0}
              value={bank.count}
              onChange={(e) => patch((c) => ({ ...c, gate: { ...c.gate, bank: { ...c.gate.bank, count: Math.max(0, Number(e.target.value) || 0) } } }))}
              className={`${INPUT} w-[64px] py-1 text-right font-mono text-[12px]`}
            />
            <span>{plural(bank.count, "вопрос", "вопроса", "вопросов")}</span>
          </div>
        </div>
        <BankImport bank={bank.name} />
      </div>
    </section>
  );
}
